import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import {
  Check,
  Eye,
  FilePlus2,
  FileText,
  Loader2,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import {
  useApplicationDocuments,
  useDecideApplication,
  useDocumentPresets,
  useRequestDocuments,
  useReviewDocument,
} from "@/hooks/api/useApplications";

const REQUESTABLE = new Set(["viewing_done", "documents_requested"]);
const DECIDABLE = new Set(["documents_received", "screening"]);

export function RequestModal({
  applicationId,
  incomeType,
  onClose,
}: {
  applicationId: string;
  incomeType: string | null;
  onClose: () => void;
}) {
  const { data: presets } = useDocumentPresets(incomeType ?? undefined);
  const request = useRequestDocuments();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [custom, setCustom] = useState("");
  const [initialised, setInitialised] = useState(false);

  if (presets && !initialised) {
    setChecked(Object.fromEntries(presets.map((p) => [p.subject, p.is_required])));
    setInitialised(true);
  }

  const send = () => {
    const items = [
      ...(presets ?? [])
        .filter((p) => checked[p.subject])
        .map((p) => ({
          subject: p.subject,
          is_required: p.is_required,
          note: p.note,
          is_standard: true,
        })),
      ...(custom.trim()
        ? [{ subject: custom.trim(), is_required: true, is_standard: false }]
        : []),
    ];
    if (items.length === 0) return;
    request.mutate(
      { applicationId, items },
      {
        onSuccess: (created) => {
          toast.success(`Document request sent (${created.length} items)`);
          onClose();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-medium">Request documents</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Standard set for{" "}
                {incomeType ? incomeType.replaceAll("_", " ") : "this candidate"} —
                adjust as needed.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="space-y-2">
            {(presets ?? []).map((p) => (
              <label
                key={p.subject}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={checked[p.subject] ?? false}
                  onChange={(e) =>
                    setChecked((c) => ({ ...c, [p.subject]: e.target.checked }))
                  }
                  className="mt-0.5 size-4 accent-primary"
                />
                <span className="min-w-0">
                  <span className="font-medium">{p.subject}</span>{" "}
                  <Badge tone={p.is_required ? "info" : "neutral"}>
                    {p.is_required ? "Required" : "Optional"}
                  </Badge>
                  {p.note && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {p.note}
                    </span>
                  )}
                </span>
              </label>
            ))}
            <Input
              placeholder="+ Add a custom document…"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={request.isPending} onClick={send}>
              <FilePlus2 className="size-4" />
              Send document request
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Dossier tab "Documenten" (journeys 5.1/5.3): request set, review, decide. */
export function DocumentsCard({
  applicationId,
  status,
  incomeType,
}: {
  applicationId: string;
  status: string;
  incomeType: string | null;
}) {
  const { data: documents } = useApplicationDocuments(applicationId);
  const review = useReviewDocument();
  const decide = useDecideApplication();
  const [showRequest, setShowRequest] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const requirements = documents ?? [];
  const required = requirements.filter((r) => r.is_required);
  const allAccepted =
    required.length > 0 && required.every((r) => r.status === "accepted");

  const doReview = (
    requirementId: string,
    accept: boolean,
    rejectReason?: string,
  ) =>
    review.mutate(
      { applicationId, requirementId, accept, rejectReason },
      {
        onSuccess: () => {
          toast.success(accept ? "Document accepted" : "Sent back to the candidate");
          setRejecting(null);
          setReason("");
        },
        onError: (err) => toast.error(err.message),
      },
    );

  const doDecide = (approve: boolean) =>
    decide.mutate(
      { applicationId, approve },
      {
        onSuccess: () =>
          toast.success(approve ? "Candidate approved" : "Candidate rejected"),
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="size-4" /> Documents
        </CardTitle>
        {REQUESTABLE.has(status) && (
          <Button size="sm" variant="outline" onClick={() => setShowRequest(true)}>
            <FilePlus2 className="size-4" />
            Request documents
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {allAccepted && DECIDABLE.has(status) && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <span className="font-medium text-emerald-800">
              All required documents accepted — you can decide on this candidate.
            </span>
            <div className="flex gap-2">
              <Button size="sm" disabled={decide.isPending} onClick={() => doDecide(true)}>
                <ThumbsUp className="size-4" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={decide.isPending}
                onClick={() => doDecide(false)}
              >
                <ThumbsDown className="size-4" /> Reject
              </Button>
            </div>
          </div>
        )}

        {requirements.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {REQUESTABLE.has(status)
              ? "No documents requested yet."
              : "Documents become available once the candidate confirms interest after the viewing."}
          </p>
        ) : (
          <ul className="space-y-3">
            {requirements.map((r) => (
              <li key={r.id} className="rounded-lg border border-border p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {r.subject}{" "}
                    {!r.is_required && (
                      <span className="text-xs font-normal text-muted-foreground">
                        (optional)
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    {r.status === "submitted" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={review.isPending}
                          onClick={() => doReview(r.id, true)}
                        >
                          <Check className="size-4" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={review.isPending}
                          onClick={() => setRejecting(rejecting === r.id ? null : r.id)}
                        >
                          <X className="size-4" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {r.reject_reason && (
                  <p className="mt-1.5 text-xs text-destructive">
                    Rejected: {r.reject_reason}
                  </p>
                )}
                {r.files.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {r.files.map((f) => (
                      <li key={f.id}>
                        {f.url ? (
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted/70"
                          >
                            <Eye className="size-3.5" /> {f.file_name}
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {f.file_name}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {rejecting === r.id && (
                  <div className="mt-2 flex gap-2">
                    <Input
                      placeholder="Reason (sent to the candidate)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <Button
                      size="sm"
                      disabled={review.isPending || !reason.trim()}
                      onClick={() => doReview(r.id, false, reason.trim())}
                    >
                      {review.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        "Send back"
                      )}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {showRequest && (
        <RequestModal
          applicationId={applicationId}
          incomeType={incomeType}
          onClose={() => setShowRequest(false)}
        />
      )}
    </Card>
  );
}
